/* __next_internal_client_entry_do_not_use__ Default,default auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef, useMemo } from 'react';
import { Formik, Form, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { tv } from 'tailwind-variants';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { sendGTMEvent } from '@next/third-parties/google';
import { withStandardComponentWrapper } from 'helpers/HOC';
import { renderFormField } from 'helpers/FormFields';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import { apiRequest } from 'lib/utils/apiWrapper';
import { useSitecore } from '@sitecore-content-sdk/nextjs';
import { FORM_CONSTANTS } from 'helpers/Constants/SiteConfig';
import { RemixIcon } from 'helpers/RemixIcon';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { getContentGroups } from 'lib/utils/gtm-utils';
import { usePathname } from 'next/navigation';
// Helper function to recursively process fields including section fields
const processFieldsRecursively = (formFields = [], processor)=>{
    formFields.forEach((field)=>{
        const fieldConfig = field.fields;
        const fieldType = fieldConfig?.fieldType?.value;
        if (fieldType === 'section') {
            // Process section fields recursively
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const sectionFields = fieldConfig?.selectOptionsAndSection || [];
            processFieldsRecursively(sectionFields, processor);
        } else {
            // Process regular field
            processor(field);
        }
    });
};
// Generate initial values from form fields
const generateInitialValues = (formFields = [])=>{
    const initialValues = {};
    processFieldsRecursively(formFields, (field)=>{
        const fieldConfig = field.fields;
        const fieldName = fieldConfig?.fieldName?.value || fieldConfig?.fieldId?.value;
        const fieldType = fieldConfig?.fieldType?.value?.toLowerCase();
        if (fieldName) {
            // Handle recaptcha field type with empty string initial value
            if (fieldType === 'recaptcha') {
                initialValues[fieldName] = '';
            } else if (fieldType === 'header') {
                // Handle header field type with default value
                initialValues[fieldName] = fieldConfig?.defaultValue?.value || '';
            } else {
                initialValues[fieldName] = fieldConfig?.defaultValue?.value || '';
            }
        }
    });
    return initialValues;
};
/** Resolves the Formik field key for the visitor / infinity UUID hidden field (Sitecore name may differ in casing). */ const getVisitorIdFieldKey = (formFields)=>{
    const initial = generateInitialValues(formFields);
    return Object.keys(initial).find((k)=>k.toLowerCase() === 'infinityuuid') ?? null;
};
async function resolveVisitorIdValue() {
    if ("object" === 'undefined' || typeof window._its !== 'function') {
        return null;
    }
    try {
        const value = await window._its('getVisitorId');
        return `${value}`;
    } catch (error) {
        console.error('Error setting vid:', error);
        return null;
    }
}
/** Keeps Formik state in sync with `window._its` so the POST payload includes the visitor id (DOM-only updates do not). */ const VisitorIdFormikSync = ({ fieldName })=>{
    const { setFieldValue } = useFormikContext();
    useEffect(()=>{
        let cancelled = false;
        void resolveVisitorIdValue().then((id)=>{
            if (cancelled || !id) {
                return;
            }
            setFieldValue(fieldName, id, false);
        });
        return ()=>{
            cancelled = true;
        };
    }, [
        fieldName,
        setFieldValue
    ]);
    return null;
};
// Generate validation schema from form fields
const generateValidationSchema = (formFields = [])=>{
    const validationFields = {};
    processFieldsRecursively(formFields, (field)=>{
        const fieldConfig = field.fields;
        const fieldName = fieldConfig?.fieldName?.value || fieldConfig?.fieldId?.value;
        const fieldType = fieldConfig?.fieldType?.value;
        if (fieldName) {
            // Skip validation for header fields (they're hidden with default values)
            if (fieldType?.toLowerCase() === 'header') {
                return;
            }
            let fieldValidation = Yup.string();
            // Required validation
            if (fieldConfig?.required?.value) {
                fieldValidation = fieldValidation.required(fieldConfig?.requiredValidationMessage?.value || `${fieldConfig?.label?.value} is required`);
            } else {
                // If not required, allow empty strings
                fieldValidation = fieldValidation.optional();
            }
            // Email validation
            if (fieldType === 'email') {
                fieldValidation = fieldValidation.email(fieldConfig?.validationErrorMessage?.value || 'Please enter a valid email address');
            }
            // reCAPTCHA v3 validation
            if (fieldType === 'recaptcha') {
                if (fieldConfig?.required?.value) {
                    fieldValidation = fieldValidation.required(fieldConfig?.requiredValidationMessage?.value || 'Please complete the reCAPTCHA verification');
                }
            }
            // Pattern validation
            if (fieldConfig?.validationPattern?.value && fieldConfig.validationPattern.value.trim() !== '') {
                try {
                    const regex = new RegExp(fieldConfig.validationPattern.value);
                    fieldValidation = fieldValidation.matches(regex, fieldConfig?.validationErrorMessage?.value || 'Invalid format');
                } catch (_error) {
                    console.warn('Invalid regex pattern:', fieldConfig.validationPattern.value);
                }
            }
            // Character limit validation - only apply if values are valid numbers
            const minLimit = fieldConfig?.minCharacterLimit?.value;
            const maxLimit = fieldConfig?.characterLimit?.value;
            if (minLimit && typeof minLimit === 'number' && minLimit > 0) {
                fieldValidation = fieldValidation.min(minLimit, fieldConfig?.minCharacterLimitValidationMessage?.value || `Minimum ${minLimit} characters required`);
            }
            if (maxLimit && typeof maxLimit === 'number' && maxLimit > 0) {
                fieldValidation = fieldValidation.max(maxLimit, fieldConfig?.maxCharacterLimitValidationMessage?.value || `Maximum ${maxLimit} characters allowed`);
            }
            validationFields[fieldName] = fieldValidation;
        }
    });
    return Yup.object().shape(validationFields);
};
/** Required user-visible fields (excludes header, reCAPTCHA — token is applied on submit). */ const areRequiredInputFieldsFilled = (values, formFields)=>{
    let filled = true;
    processFieldsRecursively(formFields, (field)=>{
        const fieldConfig = field.fields;
        const fieldType = fieldConfig?.fieldType?.value?.toLowerCase();
        if (fieldType === 'header' || fieldType === 'recaptcha') {
            return;
        }
        const fieldName = fieldConfig?.fieldName?.value || fieldConfig?.fieldId?.value;
        if (!fieldName || !fieldConfig?.required?.value) {
            return;
        }
        const val = values[fieldName];
        if (val === undefined || val === null || String(val).trim() === '') {
            filled = false;
        }
    });
    return filled;
};
const formHasRequiredInputFields = (formFields)=>{
    let hasRequired = false;
    processFieldsRecursively(formFields, (field)=>{
        const fieldConfig = field.fields;
        const fieldType = fieldConfig?.fieldType?.value?.toLowerCase();
        if (fieldType === 'header' || fieldType === 'recaptcha') {
            return;
        }
        const fieldName = fieldConfig?.fieldName?.value || fieldConfig?.fieldId?.value;
        if (fieldName && fieldConfig?.required?.value) {
            hasRequired = true;
        }
    });
    return hasRequired;
};
// Inner component that has access to reCAPTCHA context
const DynamicFormInner = (props)=>{
    const { page } = useSitecore();
    //const router = useRouter();
    //const pathname = router.asPath?.split('?')[0] ?? '';
    const pathname = usePathname();
    const route = page?.layout?.sitecore?.route;
    const pageItemId = route?.itemId;
    const layoutContext = page?.layout?.sitecore?.context;
    const reCAPTCHASiteKey = layoutContext?.SiteSettings?.reCAPTCHASiteKey || process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '';
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [submitStatus, setSubmitStatus] = useState('idle');
    const { executeRecaptcha } = useGoogleReCaptcha();
    const successContainerRef = useRef(null);
    const fields = props.fields;
    const formFields = useMemo(()=>fields?.formFields || [], [
        fields?.formFields
    ]);
    const initialValues = useMemo(()=>generateInitialValues(formFields), [
        formFields
    ]);
    const visitorIdFieldName = useMemo(()=>getVisitorIdFieldKey(formFields), [
        formFields
    ]);
    useEffect(()=>{
        if (isSubmitted && submitStatus === 'success') {
            successContainerRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
        }
    }, [
        isSubmitted,
        submitStatus
    ]);
    if (!fields) {
        return /*#__PURE__*/ _jsx("div", {
            children: "No form data available"
        });
    }
    // Extract language codes from Sitecore context
    const languageCodes = layoutContext?.languages?.map((lang)=>lang.isoCode.toLowerCase()) || [];
    // Extract content groups from pathname
    const { content_group, content_group2 } = getContentGroups(pathname ?? '', languageCodes);
    // Check if form has recaptcha field
    const hasRecaptchaField = formFields.some((field)=>{
        const fieldTypeValue = typeof field.fields?.fieldType === 'object' && 'value' in field.fields.fieldType ? field.fields.fieldType.value : '';
        const fieldType = typeof fieldTypeValue === 'string' ? fieldTypeValue.toLowerCase() : '';
        return fieldType === 'recaptcha';
    });
    // Get recaptcha field name
    const getRecaptchaFieldName = ()=>{
        let recaptchaFieldName = null;
        processFieldsRecursively(formFields, (field)=>{
            const fieldTypeValue = typeof field.fields?.fieldType === 'object' && 'value' in field.fields.fieldType ? field.fields.fieldType.value : '';
            const fieldType = typeof fieldTypeValue === 'string' ? fieldTypeValue.toLowerCase() : '';
            if (fieldType === 'recaptcha') {
                const fieldNameValue = typeof field.fields?.fieldName === 'object' && 'value' in field.fields.fieldName ? field.fields.fieldName.value : null;
                const fieldIdValue = typeof field.fields?.fieldId === 'object' && 'value' in field.fields.fieldId ? field.fields.fieldId.value : null;
                recaptchaFieldName = typeof fieldNameValue === 'string' ? fieldNameValue : typeof fieldIdValue === 'string' ? fieldIdValue : null;
            }
        });
        return recaptchaFieldName;
    };
    const handleSubmit = async (values, { setSubmitting, setStatus, setFieldValue })=>{
        setSubmitStatus('loading');
        setStatus(null); // Clear previous status
        if (visitorIdFieldName && !String(values[visitorIdFieldName] ?? '').trim()) {
            const visitorId = await resolveVisitorIdValue();
            if (visitorId) {
                values[visitorIdFieldName] = visitorId;
                setFieldValue(visitorIdFieldName, visitorId);
            }
        }
        // Generate reCAPTCHA token before submitting if recaptcha field exists
        if (hasRecaptchaField && executeRecaptcha) {
            const recaptchaFieldName = getRecaptchaFieldName();
            if (recaptchaFieldName) {
                try {
                    console.log('Generating fresh reCAPTCHA token before submission...');
                    const token = await executeRecaptcha('submit_form');
                    console.log('Token generated:', token ? 'Success' : 'Failed');
                    // Update the form values with the new token
                    values[recaptchaFieldName] = token;
                    setFieldValue(recaptchaFieldName, token);
                } catch (error) {
                    console.error('Failed to generate reCAPTCHA token:', error);
                    setSubmitStatus('error');
                    setStatus('error');
                    setSubmitting(false);
                    return;
                }
            }
        }
        // Collect header fields from form fields
        const headerFields = [];
        processFieldsRecursively(formFields, (field)=>{
            const fieldConfig = field.fields;
            const fieldType = fieldConfig?.fieldType?.value?.toLowerCase();
            const fieldName = fieldConfig?.fieldName?.value || fieldConfig?.fieldId?.value;
            if (fieldType === 'header' && fieldName && values[fieldName] !== undefined) {
                headerFields.push({
                    name: fieldName,
                    value: values[fieldName]
                });
            }
        });
        // Prepare payload with header fields if any exist
        const payload = {
            ...values,
            ...(headerFields.length > 0 && {
                headerFields
            })
        };
        try {
            const pageTitle = window.document.title?.trim();
            // Simulate API call
            const response = await apiRequest(FORM_CONSTANTS.ENDPOINT_URL, {
                method: 'POST',
                data: payload,
                headers: {
                    endpointURL: fields.submitEndpoint?.value || '',
                    sourceSystem: FORM_CONSTANTS.SOURCE_SYSTEM,
                    referringPageURL: window.location.href,
                    referringItemID: pageItemId || '',
                    referringPageTitle: !pageTitle || pageTitle === 'Page' ? 'IM | Contact Us' : pageTitle
                }
            });
            if (response.success) {
                setSubmitStatus('success');
                setIsSubmitted(true);
                setStatus('success');
                // Send GTM form_submit event ONLY on successful form submission
                sendGTMEvent({
                    event: 'form_submit',
                    'gtm.element.dataset.gtmComponentName': props?.rendering?.componentName,
                    enquiry_type: values?.enquiryType || values.enquiry_type || '',
                    form_name: 'Contact Us',
                    content_group,
                    content_group2
                });
            } else {
                setSubmitStatus('error');
                setStatus('error'); // Set Formik status to trigger reCAPTCHA regeneration
            }
        } catch (_error) {
            setSubmitStatus('error');
            setStatus('error'); // Set Formik status to trigger reCAPTCHA regeneration
        } finally{
            setSubmitting(false);
        }
    };
    const { container, successMessage, Message, MessageText, form, fieldGroup, submitButton, submitButtonContainer, successContainer, successCTA } = TAILWIND_VARIANTS();
    return /*#__PURE__*/ _jsx("div", {
        ref: successContainerRef,
        children: isSubmitted && submitStatus === 'success' ? /*#__PURE__*/ _jsxs("div", {
            className: successContainer(),
            children: [
                fields?.successGeneralMessage && /*#__PURE__*/ _jsxs("div", {
                    className: Message({
                        submitStatus: submitStatus
                    }),
                    children: [
                        /*#__PURE__*/ _jsx(RemixIcon, {
                            name: "checkbox-circle-line",
                            size: "md"
                        }),
                        /*#__PURE__*/ _jsx(PlainTextWrapper, {
                            field: fields.successGeneralMessage?.fields?.Phrase,
                            className: MessageText({
                                submitStatus: submitStatus
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(RichTextWrapper, {
                    field: fields.successMessage,
                    tag: "div",
                    className: successMessage()
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: successCTA(),
                    children: fields?.successCTA?.value?.href ? /*#__PURE__*/ _jsx(LinkWrapper, {
                        field: fields.successCTA,
                        ctaComponentClass: "default",
                        ctaVariant: "secondary",
                        suppressNewTabIcon: true,
                        onClick: ()=>{
                            setIsSubmitted(false);
                            setSubmitStatus('idle');
                        }
                    }) : /*#__PURE__*/ _jsx(ButtonWrapper, {
                        text: fields.successCTA?.value?.title || fields.successCTA?.value?.text || 'Start a new request',
                        ctaComponentClass: "default",
                        ctaVariant: "secondary",
                        onClick: ()=>{
                            setIsSubmitted(false);
                            setSubmitStatus('idle');
                        }
                    })
                })
            ]
        }) : /*#__PURE__*/ _jsx("div", {
            className: container(),
            children: /*#__PURE__*/ _jsx(Formik, {
                initialValues: initialValues,
                validationSchema: generateValidationSchema(formFields),
                validateOnMount: true,
                onSubmit: handleSubmit,
                children: ({ errors, touched, isSubmitting, values, dirty })=>{
                    const recaptchaName = getRecaptchaFieldName();
                    const hasBlockingErrors = Object.entries(errors).some(([key, err])=>Boolean(err) && key !== recaptchaName);
                    const requiredFilled = areRequiredInputFieldsFilled(values, formFields);
                    const optionalOnlyForm = !formHasRequiredInputFields(formFields);
                    const submitDisabled = isSubmitting || hasBlockingErrors || !requiredFilled || optionalOnlyForm && !dirty;
                    return /*#__PURE__*/ _jsxs(Form, {
                        className: form(),
                        children: [
                            visitorIdFieldName ? /*#__PURE__*/ _jsx(VisitorIdFormikSync, {
                                fieldName: visitorIdFieldName
                            }) : null,
                            /*#__PURE__*/ _jsx("div", {
                                className: fieldGroup(),
                                children: formFields.map((field)=>renderFormField(field, errors, touched, reCAPTCHASiteKey))
                            }),
                            submitStatus === 'error' && /*#__PURE__*/ _jsxs("div", {
                                className: Message(),
                                children: [
                                    /*#__PURE__*/ _jsx(RemixIcon, {
                                        name: "alert-line",
                                        size: "md"
                                    }),
                                    /*#__PURE__*/ _jsx(RichTextWrapper, {
                                        field: fields.failureMessage,
                                        className: MessageText({
                                            submitStatus: submitStatus
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: submitButtonContainer(),
                                children: /*#__PURE__*/ _jsx(ButtonWrapper, {
                                    text: fields.submitButtonText?.value || 'Submit',
                                    ctaComponentClass: "default",
                                    ctaVariant: "primary",
                                    remixIcon: "phone-line",
                                    remixIconSize: "md",
                                    ctaIconAlignment: "right",
                                    type: "submit",
                                    disabled: submitDisabled,
                                    className: submitButton()
                                })
                            })
                        ]
                    });
                }
            })
        })
    });
};
// Wrapper component with GoogleReCaptchaProvider
const DynamicForm = (props)=>{
    const { page } = useSitecore();
    const layoutContext = page?.layout?.sitecore?.context;
    const reCAPTCHASiteKey = layoutContext?.SiteSettings?.reCAPTCHASiteKey || process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '';
    return /*#__PURE__*/ _jsx(GoogleReCaptchaProvider, {
        reCaptchaKey: reCAPTCHASiteKey,
        children: /*#__PURE__*/ _jsx(DynamicFormInner, {
            ...props
        })
    });
};
export const Default = withStandardComponentWrapper(DynamicForm);
export default DynamicForm;
const TAILWIND_VARIANTS = tv({
    slots: {
        container: [],
        form: [
            'flex',
            'flex-col',
            'gap-general-spacing-gutter'
        ],
        fieldGroup: [
            'fieldGroup',
            'flex',
            'flex-col',
            'gap-general-spacing-gutter',
            '[&>*:last-child]:-mt-[var(--general-spacing-gutter)]'
        ],
        submitButtonContainer: [
            'flex',
            'justify-end',
            'flex-col',
            'sm:flex-row'
        ],
        submitButton: [],
        successMessage: [],
        Message: [
            'flex',
            'items-start',
            'py-spacing-spacing-16',
            'px-spacing-spacing-24',
            'gap-space-between-less',
            'rounded-component-alert-bg-radius',
            'text-color-text-text-info'
        ],
        MessageText: [
            'text-component-alert-text-priority',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'leading-typography-line-height-body-medium',
            'font-normal'
        ],
        successContainer: [
            'flex',
            'flex-col',
            'gap-general-spacing-gutter'
        ],
        successCTA: [
            'flex',
            'justify-end'
        ]
    },
    variants: {
        submitStatus: {
            error: {
                Message: [
                    'bg-component-alert-bg-priority',
                    '[&_svg]:fill-color-fill-fill-critical'
                ],
                MessageText: [
                    'text-color-text-text-info'
                ]
            },
            success: {
                Message: [
                    'bg-component-alert-by-success',
                    '[&_svg]:fill-color-fill-fill-success'
                ],
                MessageText: [
                    'text-color-text-text-success-hover'
                ]
            }
        }
    },
    defaultVariants: {
        submitStatus: 'error'
    }
});
